import { CommonModule } from "@angular/common";
import { Component, HostListener, inject, OnDestroy } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router, RouterModule } from "@angular/router";
import { ConfigService } from "@web-client/shared";
import { Observable, Subject, takeUntil } from "rxjs";
import { BookmarkService, MenuItemService, ThemeService } from "../../services";
import { BookmarkDialogComponent } from "../bookmark-dialog/bookmark-dialog.component";
import { GlobalSearchComponent } from "../global-search/global-search.component";

// Import Auth Service
import { AuthService, User } from '@web-client/auth';
import { NavGroup, NavLinksComponent } from "../navigation/nav-links.component";

@Component({
    selector: 'lib-header',
    templateUrl: './header.component.html',
    imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule, MatDividerModule, MatMenuModule, MatSnackBarModule, MatTooltipModule, MatDialogModule, NavLinksComponent],
    standalone: true,
    host: { 'class': 'w-full' },
    styles: [`
        .mobile-menu-btn {
            display: none !important;
        }
        @media (max-width: 1023px) {
            .mobile-menu-btn {
                display: flex !important;
            }
        }
    `]
})
export class HeaderComponent implements OnDestroy {
    currentUser: User | null = null;

    private readonly destroy$ = new Subject<void>();

    private readonly themeService = inject(ThemeService);
    private readonly authService = inject(AuthService);
    private readonly configService: ConfigService = inject(ConfigService);
    private readonly router: Router = inject(Router);
    private readonly bookmarkService = inject(BookmarkService);
    private readonly dialog = inject(MatDialog);
    private readonly menuItemService = inject(MenuItemService)

    categories: string[] = this.menuItemService.getMenuCategories()
    menuItems$: Observable<NavGroup[]> = this.menuItemService.getSelectedMenuItems()
    selectedCategory$: Observable<string | null> = this.menuItemService.selectedCategory$

    bookmarkSite() {
        this.bookmarkService.bookmarkPage(this.configService.appName, 'High-performance, privacy-focused developer tools for your daily workflow.');
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault();
            this.openSearch();
        }
    }

    generateBookmarkFolder() {
        this.dialog.open(BookmarkDialogComponent, {
            maxWidth: '500px',
            panelClass: 'modern-dialog'
        });
    }

    openSearch() {
        this.dialog.open(GlobalSearchComponent, {
            maxWidth: '95vw',
            maxHeight: '95vh',
            panelClass: 'full-flat-dialog',
            position: { top: '24px' }
        });
    }

    onMouseEnter(category: string) {
        this.menuItemService.activeMenuItem(category)
    }

    onMouseLeave() {
        this.menuItemService.activeMenuItem(null);
    }

    toggleSidenav() {
        const currentValue = this.themeService.sideNav$.value;

        this.themeService.sideNav$.next(({
            open: !currentValue.open,
            mode: currentValue.mode
        }))
    }

    toggleThemeStyle() {
        this.themeService.toggleTheme()
    }

    get isDarkMode() {
        return this.themeService.isDarkTheme();
    }


    constructor(
    ) {
        // Subscribe to Logto user data
        this.authService.userData$
            .pipe(takeUntil(this.destroy$))
            .subscribe(user => {
                this.currentUser = user;
            });
    }

    get coreAppUrl(): string {
        return this.configService.coreAppUrl;
    }

    get adminAppUrl(): string {
        return this.configService.adminAppUrl;
    }

    get isAdmin(): boolean {
        return this.currentUser?.role === 'admin';
    }

    get appName(): string {
        return this.configService.appName;
    }

    logout(): void {
        this.authService.signOut().subscribe(() => {
            this.currentUser = null;
            // No need to navigate - auth guard will redirect to Logto on next protected route access
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}